import React, { useState, useContext } from 'react';
import classnames from 'classnames';
import Styled from 'styled';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useMutation } from '@apollo/client/react/hooks';

import { AlertContext } from 'lib/alert-context/alert-context';
import ActionSheet, { Action } from 'four-nets/ui-v2/action-sheet/action-sheet';

import removeMutation, {
    RemoveUserBrandMutation,
    RemoveUserBrandMutationVariables,
} from './mutations/remove-user-brand.graphql';
import CreateUserBrand from '../create-user-brand/create-user-brand';
import { UserBrandFragment } from './user-brands.graphql';
import defaultStyles from './user-brand.scss';

export interface Props extends UserBrandFragment {
    styles: typeof defaultStyles;
    username: string;
    refetch: () => any;
    isAuthor: boolean;
}

type Brand = NonNullable<UserBrandFragment>['brand'];

interface ItemProps {
    styles: typeof defaultStyles;

    id: string;
    userBrand: any;
    user: UserBrandFragment['user'] | null;
    username: React.ReactNode;
    brand: Brand;
    isReply?: boolean;
    isAuthor?: boolean;

    serviceText?: string;
    description: string;

    refetch: () => any;
}

const Item: React.FC<ItemProps> = props => {
    const { styles, id, userBrand, user, username, isAuthor, description, isReply, serviceText, refetch } = props;

    const [removeFn] = useMutation<RemoveUserBrandMutation, RemoveUserBrandMutationVariables>(removeMutation);

    const [edit, setEdit] = useState(false);
    const [open, setOpen] = useState(false);
    const { confirm } = useContext(AlertContext);

    const actionSheet = (
        <ActionSheet
            open={open}
            onClose={() => {
                setOpen(false);
            }}
        >
            {({ close }) => (
                <>
                    <Action
                        onClick={() => {
                            setEdit(true);
                            setOpen(false);
                        }}
                    >
                        <FormattedMessage id='EDIT' values={{ gender: 'v' }} />
                    </Action>
                    <Action
                        onClick={() => {
                            confirm!({
                                onAccept: () => {
                                    return removeFn({ variables: { input: { id } } }).then(refetch);
                                },
                                title: <FormattedMessage id='ARE_YOU_SURE_VENDOR' />,
                                acceptButtonLabel: <FormattedMessage id='REMOVE' values={{ gender: 'v' }} />,
                                body: (
                                    <>
                                        <FormattedMessage
                                            id='ARE_YOU_SURE_TO_DELETE_RECOMENDATION_FOR_'
                                            values={{ gender: 'v' }}
                                        />
                                        {username}?
                                    </>
                                ),
                            });
                        }}
                    >
                        <FormattedMessage id='REMOVE' values={{ gender: 'v' }} />
                    </Action>

                    {close}
                </>
            )}
        </ActionSheet>
    );

    return edit ? (
        <CreateUserBrand
            open={true}
            close={() => {
                setEdit(false);
                setOpen(false);
            }}
            refetch={refetch}
            userBrand={userBrand}
        />
    ) : (
        <div className={classnames(styles.item, { [styles.reply]: isReply })}>
            {actionSheet}
            {username ? (
                <div className={styles.user}>
                    {serviceText ? <div className={styles.service}>{serviceText}</div> : null}
                    {username}
                </div>
            ) : null}
            <div className={styles.description}>{description || <FormattedMessage id='RECOMMENDS' />}</div>
            {isAuthor ? (
                <button
                    className={styles.moreButton}
                    onClick={() => {
                        setOpen(true);
                    }}
                >
                    ...
                </button>
            ) : null}
        </div>
    );
};

const UserBrand: React.FC<Props> = React.memo(props => {
    const { styles, isAuthor, refetch, createdTime, username, user, reply: replyProps, ...userBrand } = props;
    const { id, brand, serviceText, description } = userBrand;

    const linkifyIfVendor = (brand: Brand) => {
        if (brand.vendor && brand.vendor.isPresentationPaid) {
            return (
                <Link className={styles.link} to={brand.vendor.href}>
                    {brand.name}
                </Link>
            );
        }
        return <span className={styles.brand}>{brand.name}</span>;
    };

    return (
        <>
            <Item
                id={id}
                userBrand={userBrand}
                styles={styles}
                user={(brand && brand.vendor && brand.vendor.user) || null}
                brand={brand}
                description={description}
                serviceText={serviceText}
                isReply={false}
                username={linkifyIfVendor(brand)}
                isAuthor={isAuthor}
                refetch={refetch}
            />
        </>
    );
});

export default Styled(defaultStyles)(UserBrand);
